<!-- src/views/bible/index.vue -->
<template>
    <section class="container">
        <div class="card">
            <div class="card-body">
                <div class="row justify-content-end">
                    <div class="col-7">
                        <div class="btn-group mx-2" role="group">
                            <template v-for="version in versions" :key="version">
                                <input type="radio" class="btn-check" :id="version" :value="version" v-model="selectedVersion" @change="loadBooks" />
                                <label class="btn btn-sm btn-outline-primary" :for="version">{{ version }}</label>
                            </template>
                        </div>
                        <div class="input-group">
                            <input type="search" class="form-control" placeholder="Livro, Capítulo, Palavras..." v-model="searchQuery" @input="performSearch" autofocus />
                            <select class="form-control" v-model="selectedTestament" @change="filterBooks">
                                <option value="all">Todos</option>
                                <option value="old">Antigo Testamento</option>
                                <option value="new">Novo Testamento</option>
                            </select>
                            <select class="form-control" v-model="selectedBook" @change="loadChapters">
                                <option value="" disabled>--- Livro ---</option>
                                <option v-for="book in filteredBooks" :key="book.abbrev" :value="book">{{ book.name }}</option>
                            </select>
                            <select class="form-control" v-model="selectedChapter" @change="loadVerses" :disabled="!selectedBook">
                                <option v-for="(chapter, index) in (selectedBook?.chapters || [])" :key="index" :value="index + 1">Capítulo {{ index + 1 }}</option>
                            </select>
                            <button class="btn btn-primary" @click="toggleAudio">
                                <i :class="isPlaying ? 'fa-solid fa-pause' : 'fa-solid fa-play'"></i>
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="searchResults.length">
                    <div class="d-flex justify-content-between align-items-center py-1 my-4">
                        <h2 class="fs-4">Resultados</h2>
                        <Pagination :current-page="currentPage" :total-pages="totalPages" @changePage="changePage" />
                    </div>
                    <ul class="list-group">
                        <li class="list-group-item" v-for="(result, index) in paginatedResults" :key="index">
                            <strong>{{ result.book }}</strong> Capítulo {{ result.chapter }}, Verso {{ result.verse }}:
                            {{ result.text }}
                        </li>
                    </ul>
                </div>

                <VersesDisplay v-else-if="verses.length" :book="selectedBook" :chapter="selectedChapter" :verses="verses" @changeChapter="changeChapter" />

                <div v-else-if="error" class="error text-center py-5">{{ error }}</div>
                <div v-else class="text-center py-5">Selecione o livro</div>
            </div>
        </div>
    </section>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useAppStore } from "@/stores/appStore";
import { useBibleStore } from "@/stores/bibleStore";
import Pagination from "@/components/Pagination";
import VersesDisplay from "@/components/VersesDisplay";

let isFirstVerseInChapter = true;

const bibleStore = useBibleStore();

const versions = ref([]);
const defaultVersion  = ref('nvi');
const selectedVersion = ref(null);
const books = ref([]);
const selectedBook = ref(null);
const filteredBooks = ref([]);
const selectedTestament = ref('all');
const selectedChapter = ref(null);
const verses = ref([]);
const loading = ref(false);
const error = ref(null);

const searchQuery = ref('');
const searchResults = ref([]);

const currentPage = ref(1);
const itemsPerPage = 10;
const totalResults = ref(0);

const isPlaying = ref(false);
const currentVerseIndex = ref(0);
const currentUtterance = ref(null);

const loadVersions = async () => {
    try {
        loading.value = true;
        await bibleStore.getVersions();
        versions.value = bibleStore.versions;

        if (versions.value.includes(defaultVersion.value)) {
            selectedVersion.value = defaultVersion.value;
        } else if (versions.value.length > 0) {
            selectedVersion.value = versions.value[0];
        }
    } catch (err) {
        error.value = "Erro ao carregar versões.";
    } finally {
        loading.value = false;
    }
};

const loadBooks = async () => {
    if (!selectedVersion.value) return;

    try {
        loading.value = true;
        await bibleStore.getBooks(selectedVersion.value);
        books.value = bibleStore.books;
        selectedBook.value = books.value[0];
        filterBooks();
        loadVerses();
        performSearch();
    } catch (err) {
        error.value = `Erro ao carregar livros para a versão ${selectedVersion.value}`;
    } finally {
        loading.value = false;
    }
};

const filterBooks = () => {
    if (selectedTestament.value === 'all') {
        filteredBooks.value = books.value;
    } else if (selectedTestament.value === 'old') {
        filteredBooks.value = books.value.filter((book, index) => index < 39);
    } else {
        filteredBooks.value = books.value.filter((book, index) => index >= 39);
    }
};

const loadChapters = () => {
    if (!selectedBook.value) return;
    selectedChapter.value = 1;
    loadVerses();
};

const loadVerses = () => {
    if (!selectedChapter.value || !selectedBook.value) return;
    loading.value = true;
    error.value = null;
    try {
        verses.value = selectedBook.value.chapters[selectedChapter.value - 1];
    } catch (err) {
        error.value = 'Falha ao carregar versículos.';
    } finally {
        loading.value = false;
    }
};

const performSearch = () => {
  const query = searchQuery.value.toLowerCase();
  searchResults.value = [];

  if (!query) return;

  books.value.forEach((book) => {
    book.chapters.forEach((chapter, chapterIndex) => {
      chapter.forEach((verse, verseIndex) => {
        if (
          book.name.toLowerCase().includes(query) ||
          verse.toLowerCase().includes(query)
        ) {
          searchResults.value.push({
            book: book.name,
            chapter: chapterIndex + 1,
            verse: verseIndex + 1,
            text: verse,
          });
        }
      });
    });
  });

  totalResults.value = searchResults.value.length;
};

const paginatedResults = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  return searchResults.value.slice(start, start + itemsPerPage);
});

const totalPages = computed(() => Math.ceil(searchResults.value.length / itemsPerPage));

const changeChapter = (direction) => {
  if (direction === 'prev' && selectedChapter.value > 1) {
    selectedChapter.value--;
  } else if (direction === 'next' && selectedChapter.value < selectedBook.value.chapters.length) {
    selectedChapter.value++;
  }
  loadVerses();
};

const changePage = (direction) => {
  if (direction === 'prev' && currentPage.value > 1) {
    currentPage.value -= 1;
  } else if (direction === 'next' && currentPage.value < totalResults.value / itemsPerPage) {
    currentPage.value += 1;
  }
};

const playAllVerses = () => {
  verses.value.forEach((verse, index) => {
    narrateVerse(index, verse);
  });
};

const narrateVerse = (verseIndex, verseText) => {
  return new Promise((resolve) => {
    currentVerseIndex.value = verseIndex; // Atualiza o índice do verso atual

    const utterance = new SpeechSynthesisUtterance();
    currentUtterance.value = utterance; // Salva a referência atual

    if (isFirstVerseInChapter) {
      utterance.text = `${selectedBook.value.name}, capítulo ${selectedChapter.value}. Versículo ${verseIndex + 1}: ${verseText}`;
      isFirstVerseInChapter = false; // Atualiza para evitar repetir
    } else {
      // Para os versículos subsequentes, apenas o texto
      utterance.text = `Versículo ${verseIndex + 1}: ${verseText}`;
    }

    utterance.voices = 'Microsoft Francisca Online (Natural) - Portuguese (Brazil)';
    utterance.lang = 'pt-BR';
    utterance.rate = 1.1;

    utterance.onend = resolve; // Resolve após o término
    utterance.onerror = resolve; // Resolve em caso de erro

    speechSynthesis.speak(utterance);
  });
};

const toggleAudio = () => {
  if (isPlaying.value) {
    pauseAudio();
  } else {
    playAudio();
  }
};

const playAudio = async () => {
  isPlaying.value = true;
  isFirstVerseInChapter = true; // Reinicia ao começar a narrar o áudio

  for (let i = currentVerseIndex.value; i < verses.value.length; i++) {
    if (!isPlaying.value) break; // Para caso o áudio seja pausado
    const verse = verses.value[i];
    await narrateVerse(i, verse);
  }

  // Reseta ao final da narração
  if (isPlaying.value) {
    isPlaying.value = false;
    currentVerseIndex.value = 0;
  }
};

const pauseAudio = () => {
  isPlaying.value = false;

  if (currentUtterance.value) {
    speechSynthesis.cancel(); // Cancela apenas o que está em andamento
  }
};

watch(selectedVersion, loadBooks);
onMounted(() => {
    loadVersions();
});
</script>

<style scoped></style>